import { Fragment, useMemo, useState } from "react";
import PropTypes from "prop-types";

import "../../../styles/SetupStudentSelection.scss";
import "../../../styles/SetupDifferentiation.scss";

const SetupStudentSelection = ({ allStudents, studentsWithDiff, selectedStudentIds, setSelectedStudentIds }) => {
    const [filterText, setFilterText] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "lastName", direction: "ascending" });

    const studentsWithDiffIds = useMemo(() => {
        return new Set(studentsWithDiff.map((s) => s.key));
    }, [studentsWithDiff]);

    const filteredData = useMemo(() => {
        if (!filterText) return allStudents;
        return allStudents.filter((item) =>
            Object.keys(item).some((key) => item[key]?.toString().toLowerCase().includes(filterText.toLowerCase()))
        );
    }, [allStudents, filterText]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;
        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key]?.toString().toLowerCase();
            let bValue = b[sortConfig.key]?.toString().toLowerCase();
            return sortConfig.direction === "ascending" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue);
        });
    }, [filteredData, sortConfig]);

    // Group sorted/filtered data by teacher
    const groupedSortedData = useMemo(() => {
        const groups = {};
        sortedData.forEach((student) => {
            const isHomeroom = student.source === "homeroom" || !student.source;
            const teacherKey = isHomeroom
                ? `${student.homeroomTeacher || "Unknown"} - ${student.homeroomGrade || "Unknown"}`
                : `Shared: ${student.sharedGroupOwner || "Unknown"} - ${student.sharedGroupName || "Unknown"}`;
            if (!groups[teacherKey]) {
                groups[teacherKey] = [];
            }
            groups[teacherKey].push(student);
        });

        const sortedGroups = {};
        const homeroomKeys = Object.keys(groups)
            .filter((k) => !k.startsWith("Shared:"))
            .sort((a, b) => a.localeCompare(b));
        const sharedKeys = Object.keys(groups)
            .filter((k) => k.startsWith("Shared:"))
            .sort((a, b) => a.localeCompare(b));

        [...homeroomKeys, ...sharedKeys].forEach((key) => {
            sortedGroups[key] = groups[key];
        });

        return sortedGroups;
    }, [sortedData]);

    const requestSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending"
        }));
    };

    const renderSortIcons = (columnKey) => {
        if (sortConfig.key === columnKey) {
            if (sortConfig.direction === "ascending") {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up-fill" style={{ height: "16px" }} />
                        <i className="bi bi-caret-down" style={{ height: "16px", marginBottom: "5px" }} />
                    </span>
                );
            } else {
                return (
                    <span className="icon-container">
                        <i className="bi bi-caret-up" style={{ height: "16px" }} />
                        <i className="bi bi-caret-down-fill" style={{ height: "16px", marginBottom: "5px" }} />
                    </span>
                );
            }
        }
        return (
            <span className="icon-container">
                <i className="bi bi-caret-up" style={{ height: "16px" }} />
                <i className="bi bi-caret-down" style={{ height: "16px", marginBottom: "5px" }} />
            </span>
        );
    };

    const handleToggle = (studentId) => {
        setSelectedStudentIds((prev) =>
            prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId]
        );
    };

    const handleSelectAll = () => {
        const availableIds = sortedData.filter((s) => !studentsWithDiffIds.has(s.key)).map((s) => s.key);

        if (selectedStudentIds.length === availableIds.length) {
            setSelectedStudentIds([]);
        } else {
            setSelectedStudentIds(availableIds);
        }
    };

    return (
        <div className="setup-student-selection">
            <h5 className="setup-step-title">Select Students for Differentiation Setup</h5>
            <div className="setup-step-directions">
                <p>
                    Students with existing differentiation setup are displayed, but cannot be selected. If you need to
                    edit differentiation for those students, please click Cancel and edit/reset directly.
                </p>
            </div>

            <div className="filter-section">
                <label htmlFor="student-filter">Filter Students:</label>
                <input
                    id="student-filter"
                    type="text"
                    placeholder="Search by name or student number..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                />
            </div>

            <div className="table-container">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={
                                        selectedStudentIds.length > 0 &&
                                        selectedStudentIds.length ===
                                            sortedData.filter((s) => !studentsWithDiffIds.has(s.key)).length
                                    }
                                    aria-label="Select all available students"
                                />
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("lastName")}
                                    aria-label="Sort by last name"
                                >
                                    Last Name {renderSortIcons("lastName")}
                                </button>
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("firstName")}
                                    aria-label="Sort by first name"
                                >
                                    First Name {renderSortIcons("firstName")}
                                </button>
                            </th>
                            <th>
                                <button
                                    type="button"
                                    onClick={() => requestSort("studentNumber")}
                                    aria-label="Sort by student number"
                                >
                                    Student Number {renderSortIcons("studentNumber")}
                                </button>
                            </th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.keys(groupedSortedData).length === 0 && (
                            <tr>
                                <td colSpan={5} style={{ textAlign: "center", padding: "24px" }}>
                                    No students found.
                                </td>
                            </tr>
                        )}
                        {Object.entries(groupedSortedData).map(([teacherKey, students]) => (
                            <Fragment key={`group-${teacherKey}`}>
                                <tr key={`header-${teacherKey}`} className="teacher-group-header">
                                    <td
                                        colSpan={5}
                                        style={{ fontWeight: 700, backgroundColor: "#f8f9fa", padding: "8px" }}
                                    >
                                        {teacherKey}
                                    </td>
                                </tr>
                                {students.map((student) => {
                                    const hasDiff = studentsWithDiffIds.has(student.key);
                                    return (
                                        <tr key={student.key} className={hasDiff ? "disabled-row" : ""}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStudentIds.includes(student.key)}
                                                    onChange={() => handleToggle(student.key)}
                                                    disabled={hasDiff}
                                                    aria-label={`Select ${student.firstName} ${student.lastName}`}
                                                />
                                            </td>
                                            <td>{student.lastName}</td>
                                            <td>{student.firstName}</td>
                                            <td>{student.studentNumber}</td>
                                            <td>{hasDiff ? "Has Differentiation" : "Available"}</td>
                                        </tr>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="selection-summary">Selected: {selectedStudentIds.length} student(s)</div>
        </div>
    );
};

SetupStudentSelection.propTypes = {
    allStudents: PropTypes.array.isRequired,
    studentsWithDiff: PropTypes.array.isRequired,
    selectedStudentIds: PropTypes.array.isRequired,
    setSelectedStudentIds: PropTypes.func.isRequired
};

export default SetupStudentSelection;
